import React, { useState, useRef, useEffect } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
/**
 * Renders duration under detail-view charts.
 * - Weeks: "Duration: n weeks selected: W9, W10... View all" — click "View all" shows full breakdown per week.
 * - Months: "Duration: Jan 2025 – Nov 2026 · n months selected View all" — click shows all months.
 * - Year: "Duration: 2024, 2025 View all" — click shows all years.
 * Popover opens on click and closes when clicking outside.
 */
const DurationDisplay = ({
  dateType,
  dateRangeLabel,
  dateRangeTooltip,
  dateRangeDetail,
  monthsCount,
}) => {
  const [viewAllOpen, setViewAllOpen] = useState(false);

  const containerRef = useDetectClickOutside({ onTriggered: () => setViewAllOpen(false) });

  if (!dateRangeLabel) return null;

  // Build detail lines for popover: week = newline-separated, month = comma-separated, year = newline-separated
  const getDetailLines = () => {
    if (!dateRangeDetail) return [];
    if (dateType === 'month') return dateRangeDetail.split(', ');
    return dateRangeDetail.split('\n').filter(Boolean);
  };

  const detailLines = getDetailLines();
  const hasViewAll = (dateType === 'week' || dateType === 'month' || dateType === 'year') && detailLines.length > 0;

  const popoverContent = (
    <div className="absolute left-0 top-full mt-1 z-10 min-w-[180px] py-2 px-3 bg-white border border-gray-200 rounded-lg shadow-lg text-left">
      {detailLines.map((line, i) => (
        <div key={i} className="py-0.5 text-gray-700">
          {line}
        </div>
      ))}
    </div>
  );

  // View all trigger: click toggles popover; popover opens directly below this text
  const viewAllBlock = hasViewAll && (
    <span className="relative inline-block ml-1">
      <button
        type="button"
        onClick={() => setViewAllOpen((v) => !v)}
        className="text-primary font-medium hover:underline cursor-pointer bg-transparent border-none p-0 text-sm"
      >
        View all ▾
      </button>
      {viewAllOpen && popoverContent}
    </span>
  );

  // Centered block; popover is attached to "View all"
  const contentWrapper = (content) => (
    <div className="text-sm text-gray-700 mt-2 w-full flex justify-center" ref={containerRef}>
      <div className="text-center inline-block">
        {content}
        {viewAllBlock}
      </div>
    </div>
  );

  // Weeks: "Duration: n weeks selected: W9, W10... View all" — click shows full breakdown per week
  if (dateType === 'week') {
    return contentWrapper(<span>{dateRangeLabel}</span>);
  }

  // Months: "Duration: Jan 2025 – Nov 2026 · n months selected View all" (click)
  if (dateType === 'month') {
    return contentWrapper(
      <span>
        Duration: {dateRangeLabel}
        {monthsCount != null && ` · ${monthsCount} month${monthsCount === 1 ? '' : 's'} selected`}
      </span>
    );
  }

  // Year: "Duration: 2024, 2025 View all" (click shows years list)
  if (dateType === 'year') {
    return contentWrapper(<span>{dateRangeLabel}</span>);
  }

  // Fallback: plain "Duration (...)"
  return (
    <p className="text-sm text-gray-700 mt-2 text-center">
      Duration ({dateRangeLabel})
    </p>
  );
};

export default DurationDisplay;
